import { useEffect, useMemo, useState } from "react";
import { FormHelperText } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import {
  CALL_ADDITIONAL_DOCUMENT_TYPES,
  FORM_FIELDS,
} from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { useCallUploadAdditionalDocuments } from "@features/vessel-call/create/steps/CallAdditionalDocuments/useCallUploadAdditionalDocuments";
import { generateCallAdditionalDocumentField } from "@features/vessel-call/create/utils";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

const emptyArray: never[] = [];
const TEST_ID_BASE = "additional-documents";
const LANG_BASE = `vessel-call.${TEST_ID_BASE}`;
const fieldName = FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS;
const OTHER_SPECIFY_LABEL_KEY = `${LANG_BASE}.other-specify`;
const MAX_ADDITIONAL_DOCUMENTS = 25;

const getFileFormat = (file: File): string => {
  const extension = file.name.split(".").pop();
  return extension ? extension.toUpperCase() : file.type.toUpperCase();
};

const buildMinDate = (value?: Date | string | null) => {
  if (!value) return undefined;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const buildUploadEntriesFromDocuments = (documents: unknown[]) =>
  documents.map((doc, idx) => {
    const typedDoc = (doc ?? {}) as Record<string, unknown>;
    const fallbackFromUrl =
      typeof typedDoc.documentUrl === "string" && typedDoc.documentUrl.includes("/")
        ? typedDoc.documentUrl.split("/").pop()
        : typeof typedDoc.documentUrl === "string"
          ? typedDoc.documentUrl
          : typeof typedDoc.url === "string"
            ? typedDoc.url
            : undefined;
    const resolvedName =
      (typeof typedDoc.originalFileName === "string" && typedDoc.originalFileName.trim()) ||
      (typeof typedDoc.fileName === "string" && typedDoc.fileName.trim()) ||
      fallbackFromUrl ||
      (typeof typedDoc.name === "string" && typedDoc.name.trim()) ||
      (typeof typedDoc.documentName === "string" && typedDoc.documentName.trim()) ||
      `document-${idx + 1}`;

    return {
      name: resolvedName,
      url:
        typeof typedDoc.documentUrl === "string"
          ? typedDoc.documentUrl
          : typeof typedDoc.url === "string"
            ? typedDoc.url
            : undefined,
    };
  });

const CallAdditionalDocuments = () => {
  const { formData, updateFormData } = useOutletContext<VesselCallOutletContext>();
  const { notify } = useNotification();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();
  const documentTypeOptions = [
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.CERTIFICATE,
      label: t(`${LANG_BASE}.document-types.certificate`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.PERMIT,
      label: t(`${LANG_BASE}.document-types.permit`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.DOCUMENT,
      label: t(`${LANG_BASE}.document-types.document`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.VESSEL_MASTER_SOF,
      label: t(`${LANG_BASE}.document-types.vessel-master-sof`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.PRE_SAILING_MASTER_DECLARATION,
      label: t(`${LANG_BASE}.document-types.pre-sailing-master-declaration`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.INSURANCE,
      label: t(`${LANG_BASE}.document-types.insurance`),
    },
    {
      value: CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY,
      label: t(`${LANG_BASE}.document-types.other-specify`),
    },
  ];

  const callAdditionalDocumentFiles = useUploadStore(
    (state) => state.filesByKey[fieldName] ?? emptyArray,
  );
  const uploadsInFlight = useUploadStore(
    (state) => (state.uploadsInFlightByKey[fieldName] ?? 0) > 0,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const removeFileAtIndex = useUploadStore((state) => state.removeFileAtIndex);
  const updateFilesForKey = useUploadStore((state) => state.updateFilesForKey);

  const [error, setError] = useState("");
  const watchedDocuments = formData.watch?.(fieldName) ?? formData.getValues(fieldName) ?? [];

  useEffect(() => {
    const uploadStore = useUploadStore.getState();
    if (!Array.isArray(watchedDocuments)) return;

    const normalizedEntries = buildUploadEntriesFromDocuments(watchedDocuments);

    const currentEntries = uploadStore.getFilesForKey(fieldName);
    if (!normalizedEntries.length && currentEntries.length) return;

    const isAlreadySynchronized =
      currentEntries.length === normalizedEntries.length &&
      currentEntries.every((entry, idx) => {
        const current = entry as Record<string, unknown>;
        const next = normalizedEntries[idx];
        return current.name === next.name && current.url === next.url;
      });

    if (!isAlreadySynchronized) {
      uploadStore.setFilesForKey(fieldName, normalizedEntries, {
        mode: "replace",
        maxFiles: MAX_ADDITIONAL_DOCUMENTS,
      });
    }
  }, [formData, watchedDocuments]);

  const handleUploadSuccess = ({
    documentUrls,
    startIdx,
    files,
  }: {
    documentUrls: string[];
    startIdx: number;
    files: File[];
  }) => {
    const currentDocuments = [...(formData.getValues(fieldName) ?? [])];

    updateFilesForKey(fieldName, (prev) => {
      const nextEntries = [...prev];

      documentUrls.forEach((url, fileOffset) => {
        const at = startIdx + fileOffset;
        const uploadedFile = files[fileOffset];
        const fileName = uploadedFile?.name ?? `document-${at + 1}`;

        nextEntries[at] = {
          ...nextEntries[at],
          name: fileName,
          url,
          type: uploadedFile?.type,
        };

        currentDocuments[at] = {
          ...(currentDocuments[at] ?? {}),
          [FORM_FIELDS.DOCUMENT_URL]: url,
          [FORM_FIELDS.DOCUMENT_TYPE]: currentDocuments[at]?.[FORM_FIELDS.DOCUMENT_TYPE] ?? "",
          [FORM_FIELDS.OTHER_SPECIFY_TYPE]:
            currentDocuments[at]?.[FORM_FIELDS.OTHER_SPECIFY_TYPE] ?? "",
          [FORM_FIELDS.DOCUMENT_NAME]: currentDocuments[at]?.[FORM_FIELDS.DOCUMENT_NAME] ?? "",
          [FORM_FIELDS.ISSUE_DATE]: currentDocuments[at]?.[FORM_FIELDS.ISSUE_DATE] ?? null,
          [FORM_FIELDS.DOCUMENT_SUMMARY]:
            currentDocuments[at]?.[FORM_FIELDS.DOCUMENT_SUMMARY] ?? "",
          [FORM_FIELDS.EXPIRY_DATE]: currentDocuments[at]?.[FORM_FIELDS.EXPIRY_DATE] ?? null,
          [FORM_FIELDS.FILE_NAME]: currentDocuments[at]?.[FORM_FIELDS.FILE_NAME] ?? fileName,
          [FORM_FIELDS.FILE_FORMAT]:
            currentDocuments[at]?.[FORM_FIELDS.FILE_FORMAT] ?? getFileFormat(uploadedFile),
        };
      });

      return nextEntries;
    });

    formData.setValue(fieldName, currentDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(fieldName, currentDocuments);
  };

  const handleUploadError = (error: Error, meta: { startIdx: number; count: number }): void => {
    notify({
      title: t("vessel-management.notifications.additional-documents-upload-error-title"),
      description: t(
        "vessel-management.notifications.additional-documents-upload-error-description",
      ),
      type: "error",
    });
    console.error("Error on upload documents:", error);

    const { startIdx, count } = meta;

    const toRemoveDescending = Array.from({ length: count }, (_, i) => startIdx + i).reverse();

    toRemoveDescending.forEach((idx) => {
      removeFileAtIndex(fieldName, idx);
      handleRemoveFile(idx);
    });
  };

  const uploadAdditionalDocuments = useCallUploadAdditionalDocuments({
    handleUploadSuccess,
    handleUploadError,
  });

  const handleUpload = (incomingFiles: File[]) => {
    setError("");
    if (!incomingFiles?.length) return;

    uploadAdditionalDocuments.mutate({
      files: incomingFiles,
      startIdx: formData.getValues(fieldName)?.length ?? 0,
      certificateType: "ADDITIONAL_DOCUMENT",
      storeKey: fieldName,
    });
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");
    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    );
  };

  const handleRemoveFile = (idx: number) => {
    const currentDocuments = formData.getValues(fieldName);
    const updatedDocuments = Array.isArray(currentDocuments)
      ? currentDocuments.filter((_v: unknown, i: number) => i !== idx)
      : [];
    const updatedUploadEntries = buildUploadEntriesFromDocuments(updatedDocuments);

    formData.setValue(fieldName, updatedDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    setFilesForKey(fieldName, updatedUploadEntries, {
      mode: "replace",
      maxFiles: MAX_ADDITIONAL_DOCUMENTS,
    });
    updateFormData(fieldName, updatedDocuments);
    formData.clearErrors(fieldName);
    if (formData.formState.isSubmitted) {
      void formData.trigger(fieldName);
    }
  };

  const handleDocumentFieldChange = (key: string, value: unknown) => {
    updateFormData(key as never, value as never);

    const typeMatch = key.match(
      new RegExp(
        `^${FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS}\\.(\\d+)\\.${FORM_FIELDS.DOCUMENT_TYPE}$`,
      ),
    );
    if (typeMatch) {
      const index = Number(typeMatch[1]);
      if (value !== CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY) {
        const otherSpecifyPath = generateCallAdditionalDocumentField.otherSpecifyType(index);
        formData.setValue(otherSpecifyPath, "", {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        updateFormData(otherSpecifyPath, "");
      }
      return;
    }
  };

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const validateUploadFiles = (incomingFiles: File[]) => {
    const existingCount = useUploadStore.getState().getFilesForKey(fieldName).length;
    if (existingCount + incomingFiles.length > MAX_ADDITIONAL_DOCUMENTS) {
      return t("form-validation.max-images", { max: MAX_ADDITIONAL_DOCUMENTS });
    }
    return null;
  };

  const validateDrop = (selectedCount: number) => {
    if (callAdditionalDocumentFiles.length + selectedCount > MAX_ADDITIONAL_DOCUMENTS) {
      return t("form-validation.max-images", { max: MAX_ADDITIONAL_DOCUMENTS });
    }
    return null;
  };

  return (
    <div className="min-w-full flex flex-col gap-8 pb-8" data-testid={TEST_ID_BASE}>
      <FormProvider {...formData}>
        <FormItem
          testId="additional-remarks"
          name={FORM_FIELDS.ADDITIONAL_REMARKS}
          type="textarea"
          label={t(`${LANG_BASE}.remarks-instructions`)}
          maxChars={1000}
          minRows={3}
          onFieldChange={updateFormData}
          showCharCounter
        />
        <div key={fieldName} className={`rounded-sm border border-[#E6EDF2] relative`}>
          <FormSection title={t(`${LANG_BASE}.header`)} isFullWidth>
            <div>
              <FileUpload
                testId={`${fieldName}-upload`}
                onUpload={handleUpload}
                onReject={handleReject}
                onValidationResult={(message) => setError(message ?? "")}
                name={FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS}
                conditions={uploadConditions}
                files={callAdditionalDocumentFiles}
                setFiles={(next) => setFilesForKey(fieldName, next)}
                maxFiles={MAX_ADDITIONAL_DOCUMENTS}
                allowedExtensions={allowedExtensions}
                disabled={uploadAdditionalDocuments.isPending || uploadsInFlight}
                validateFiles={validateUploadFiles}
                validateDrop={validateDrop}
                uploadDescription={t("upload.additional-documents-upload", {
                  extensions: uploadConditions.format.join(", "),
                  size: uploadConditions.maxSizeLabel,
                })}
              />
              {error && (
                <FormHelperText
                  sx={{
                    ml: 0,
                    fontWeight: 500,
                    fontSize: 12,
                    marginY: "4px",
                    height: "20px",
                    color: "error.main",
                  }}
                >
                  {error}
                </FormHelperText>
              )}
            </div>
            <div className="flex flex-col gap-10 mt-6">
              {callAdditionalDocumentFiles.map((file, idx) => {
                const hasUploadedUrl =
                  typeof (file as { url?: unknown }).url === "string" &&
                  Boolean((file as { url?: string }).url);

                return (
                  <div
                    className="grid w-full gap-4 grid-cols-1 lg:grid-cols-2"
                    key={`${fieldName}-${idx}`}
                  >
                    <div className="lg:col-span-2" key={`${file.name}-${file.lastModified ?? idx}`}>
                      <SingleFile
                        testId={`${fieldName}-${idx}`}
                        file={file}
                        fieldName={fieldName}
                        currentFileIndex={idx}
                        setFiles={() => handleRemoveFile(idx)}
                        isLoading={uploadsInFlight && file instanceof File}
                      />
                    </div>
                    {hasUploadedUrl && (
                      <>
                        <div key={`document-type-${file.lastModified ?? idx}`}>
                          <FormItem
                            testId={`additional-documents-${idx}-document-type`}
                            name={generateCallAdditionalDocumentField.documentType(idx)}
                            type="select"
                            options={documentTypeOptions}
                            label={t(`${LANG_BASE}.document-type`)}
                            onFieldChange={handleDocumentFieldChange}
                          />
                        </div>
                        {watchedDocuments[idx]?.documentType ===
                        CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY ? (
                          <div key={`document-other-specify-${file.lastModified ?? idx}`}>
                            <FormItem
                              testId={`additional-documents-${idx}-other-specify-type`}
                              name={generateCallAdditionalDocumentField.otherSpecifyType(idx)}
                              type="text"
                              label={t(OTHER_SPECIFY_LABEL_KEY)}
                              maxChars={50}
                              onFieldChange={handleDocumentFieldChange}
                            />
                          </div>
                        ) : (
                          <div className="hidden lg:block" aria-hidden="true" />
                        )}
                        <div
                          className="lg:col-span-2"
                          key={`document-name-${file.lastModified ?? idx}`}
                        >
                          <FormItem
                            testId={`additional-documents-${idx}-document-name`}
                            name={generateCallAdditionalDocumentField.documentName(idx)}
                            type="text"
                            label={t(`${LANG_BASE}.document-name`)}
                            maxChars={50}
                            onFieldChange={handleDocumentFieldChange}
                          />
                        </div>
                        <div key={`issue-date-${file.lastModified ?? idx}`}>
                          <FormItem
                            testId={`additional-documents-${idx}-issue-date`}
                            name={generateCallAdditionalDocumentField.issueDate(idx)}
                            type="date-picker"
                            label={t(`${LANG_BASE}.issue-date`)}
                            onFieldChange={handleDocumentFieldChange}
                            timePicker={false}
                            dateConstraint="not-future"
                          />
                        </div>
                        <div key={`expiry-date-${file.lastModified ?? idx}`}>
                          <FormItem
                            testId={`additional-documents-${idx}-expiry-date`}
                            name={generateCallAdditionalDocumentField.expiryDate(idx)}
                            type="date-picker"
                            label={t(`${LANG_BASE}.expiry-date`)}
                            onFieldChange={handleDocumentFieldChange}
                            timePicker={false}
                            dateConstraint="not-past"
                            minDate={buildMinDate(watchedDocuments[idx]?.issueDate)}
                          />
                        </div>
                        <div
                          className="lg:col-span-2"
                          key={`document-summary-${file.lastModified ?? idx}`}
                        >
                          <FormItem
                            testId={`additional-documents-${idx}-document-summary`}
                            name={generateCallAdditionalDocumentField.documentSummary(idx)}
                            type="text"
                            label={t(`${LANG_BASE}.document-summary`)}
                            maxChars={300}
                            onFieldChange={handleDocumentFieldChange}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </FormSection>
        </div>
      </FormProvider>
    </div>
  );
};

export default CallAdditionalDocuments;
